import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f90ccf59-22af-4309-9fe3-b65d82f9d7de/artifacts/ssdhf0gw_IMG_1394.jpeg";
const PHONE_NUMBER = "+919971231812";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Products', path: '/products' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <nav 
      data-testid="navbar"
      className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-stone-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="navbar-logo"
            className="flex items-center gap-3"
          >
            <img 
              src={LOGO_URL} 
              alt="Aggarwal Foam Palace" 
              className="h-10 md:h-12 w-auto rounded-lg"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-xl font-bold text-stone-800 leading-tight">
                Aggarwal Foam Palace
              </h1>
              <p className="text-xs text-teal-600 font-medium">Since 1992</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-stone-600 hover:text-teal-700 font-medium transition-colors ${
                  location.pathname === link.path ? 'text-teal-700' : ''
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a 
              href={`tel:${PHONE_NUMBER}`}
              data-testid="navbar-call-btn"
            >
              <Button 
                variant="outline" 
                className="rounded-full border-teal-600 text-teal-700 hover:bg-teal-50"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
            <a 
              href="https://maps.app.goo.gl/tLGnG1nKfKTFJZHaA"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-directions-btn"
            >
              <Button className="rounded-full bg-teal-600 hover:bg-teal-700 text-white">
                <MapPin className="w-4 h-4 mr-2" />
                Get Directions
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button 
                variant="ghost" 
                size="icon"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-3 mb-8">
                  <img 
                    src={LOGO_URL} 
                    alt="Aggarwal Foam Palace" 
                    className="h-12 w-auto rounded-lg"
                  />
                  <div>
                    <h2 className="font-bold text-stone-800">Aggarwal Foam Palace</h2>
                    <p className="text-xs text-teal-600">Since 1992</p>
                  </div>
                </div>

                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-stone-100 ${
                        location.pathname === link.path 
                          ? 'text-teal-700' 
                          : 'text-stone-600 hover:text-teal-700'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>

                <div className="mt-auto space-y-3 pt-8">
                  <a 
                    href={`tel:${PHONE_NUMBER}`}
                    className="block"
                    data-testid="mobile-call-btn"
                  >
                    <Button 
                      className="w-full rounded-full bg-teal-600 hover:bg-teal-700"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </Button>
                  </a>
                  <a 
                    href="https://maps.app.goo.gl/tLGnG1nKfKTFJZHaA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid="mobile-directions-btn"
                  >
                    <Button 
                      variant="outline"
                      className="w-full rounded-full border-teal-600 text-teal-700"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Get Directions
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
